/** @module csi/table/matrix/Path */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/constants",
	"csi/core/SpecBuilder",
	"csi/def",
	"csi/heading/_base",
	"csi/heading/Spec",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/service/viewService",
	"csi/store/Nodes",
	"csi/utility/pathUtility",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"./_Processor"
], function(declare, _, csi, constants, SpecBuilder, def, csiHeading, Spec, html, locale, reflection, {extractSortValue}, Nodes, {pathCreate}, xml, Query, QueryProcessor, Processor) {

	var _bOrdered;
	var _bReadOnly;

	return declare(Processor, {
		/** @property {string} availableColumnStoreProperty - if specified, then store of available column nodes will be cached in the given view property */
		availableColumnStoreProperty: "",

		/** @property {boolean} bAllowColumnFilter - allow custom view filtering of column nodes */
		// FIX - maybe change so that by default it is true? Need to test all use cases
		//bAllowColumnFilter: false,

		// Spec: spec for column heading (optional)
		//columnHeadingSpec: null,

		// String: path to column nodes (optional)
		//columnPath: "",

		/** @property {Query} [columnPathQuery] - query to apply to column nodes */
		//columnPathQuery: null,

		/** @property {string} [columnSortAttr] - attribute with which to sort column nodes */
		//columnSortAttr: "",

		/** @property {string} [columnSortPath] - path relative to column nodes holding attribute to sort by */
		//columnSortPath: "",

		/** @property {boolean} hideLoneSubheader - set to true to force header to be single row */
		//hideLoneSubheader: false,

		/** @property {boolean} preserveVector - set to true when matrix nodes stored in vector map 1-to-1 with nodes in column path */
		preserveVector: false,

		/** @property {string} selectedColumnValueProperty - if specified, then list of columns to render will be retrieved from the given view property */
		selectedColumnValueProperty: "",

		/** @property {boolean} selectedColumnValueDefaultsToAll - by default all columns are displayed and only applies if selectedColumnValueProperty is specified*/
		selectedColumnValueDefaultsToAll: true,

		/** @property {string} [sortAttr] - attribute with which to sort path nodes */
		//sortAttr: "",

		/** @property {string} [sortPath] - path relative to path nodes holding attribute to sort by */
		//sortPath: "",

		/**
		 * Add extra styling if comparing with another view
		 * @param {Context} ctx
		 * @param {Object} params
		 */
		addColumnComparisonStyling(ctx, params) {
			const {compareData} = ctx.view;

			if (!compareData) {
				return;
			}

			const {CSS_NEW, CSS_OLD} = compareData;
			const {subs} = params;
			const columnUrl = ctx.getNodeURL();
			let css = "";

			if (this.isNewComparisonColumn(ctx, columnUrl)) {
				css = CSS_NEW;
			} else if (this.isOldComparisonColumn(ctx, columnUrl)) {
				css = CSS_OLD;
			}

			if (css) {
				this._addColumnCss(params.headerParams, css);
			}

			if (subs) {
				for (const spec of subs) {
					spec.valueParams ||= {};
					this._addColumnCss(spec.headerParams, css);
					this._addColumnCss(spec.valueParams, css);
				}
			}
		},

		addDependencyPaths: function(/*Heading*/h, /*String[]*/paths) {
			// summary: add any dependency paths necessary to process matrix
			const {columnPath, columnPathQuery} = this;

			if (columnPath) {
				const queryPaths = columnPathQuery?.getPaths();

				if (queryPaths?.length) {
					queryPaths.forEach((p) => {
						paths.push(pathCreate(columnPath, p));
					});
				} else {
					paths.push(columnPath);
				}
			}

			// if matrix uses Parent step in path and has an alternate path to use to control scope, then collect
			// heading paths and substitute with alternate path
			if (h.matrix.parentPathScope) {
				def.getHeadingDependencyPaths(h, paths, true);
			}
		},

		/**
		 * If column filtering is enabled for columnPath, then return parameters to allow filtering nodes along the path
		 */
		getColumnQueryLevelStore(ctx, h) {
			if (!this.bAllowColumnFilter || !this.columnPath) {
				return;
			}

			const rcRoot = ctx.defView.getRootClass();
			const rcColumn = rcRoot.getPathClass(this.columnPath);
			if (!rcColumn) {
				return;
			}

			const store = new Nodes({
				cachingStore: ctx.cache,
				typeNode: rcColumn.getType(),
				requestParameters: {
					"Qry.Limit.Path": xml.getUrlPath(ctx.view.urlRoot, this.columnPath)
				}
			});

			ctx.view.annotateViewStore(store);

			return store;
		},

		getColumnSpecs: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return column specifications for matrix
			const {cache, view} = ctx;
			const specs = [];
			const headingParams = h.classRendererParams;
			const restrictHeadingQuery = headingParams && headingParams.restrictQuery;

			// FIX: Can we create the Spec and sub-specs first, and only then create query specs?
			// That would help depend less on the Heading and more on Spec
			let columnNodes = view.skipColumnMatrices ? null : this._getColumnNodes(ctx, h);
			if (columnNodes) {
				const {
					availableColumnStoreProperty,
					bAllowColumnFilter,
					columnPathQuery,
					columnSortAttr,
					columnSortPath,
					selectedColumnValueDefaultsToAll,
					selectedColumnValueProperty,
				} = this;

				const selectedUrls = view.get(selectedColumnValueProperty) || [];

				// if no selections, then nothing to show
				if (selectedColumnValueProperty && !selectedColumnValueDefaultsToAll && !selectedUrls.length) {
					return [];
				}

				// matrix is N columns x 1..N rows
				let bEmpty;

				// apply column filter
				if (columnPathQuery) {
					columnNodes = QueryProcessor.applyQueryToNodes(columnPathQuery, columnNodes, cache);
				}

				// apply any column filtering
				if (bAllowColumnFilter) {
					// even though matrix may indicate it allows column filtering, still need to check for query level as the view may not support it
					// (i.e. slim view, filtering turned off, etc.)
					const ql = view.getQueryLevel(h.getKey());
					if (ql) {
						const q = new Query();
						ql.applyLevelQuery(q);
						columnNodes = QueryProcessor.applyQueryToNodes(q, columnNodes, cache);
					}
				}

				// sort column nodes
				if (columnSortAttr) {
					columnNodes = this._sortNodes(columnNodes, cache, columnSortAttr, columnSortPath);
				}

				// cache column nodes store if needed
				if (availableColumnStoreProperty) {
					const data = this.getColumnStoreData(ctx, columnNodes);

					view.set(availableColumnStoreProperty, new Nodes({
						cachingStore: cache,
						data
					}));
				}

				if (selectedUrls.length) {
					const set = new Set(selectedUrls);
					columnNodes = columnNodes.filter((node) => set.has(node.$URL));
				}

				// create specs for each column node
				ctx.forEach(columnNodes, (ctx, i) => {
					const columnUrl = ctx.getNodeURL();
					const headerParams = this._getColumnSpecHeaderParams(ctx, h, i);
					const valueParams = {
						dataset: {
							[html.DATA_CONTEXT_COLUMN_URL]: columnUrl
						}
					};

					let subs;

					if (h.isMatrixGroup) {
						subs = this._getColumnSpecSubs(ctx, h, i);

						if (!subs) {
							// if no subcolumns, then stop here
							bEmpty = true;
							return;
						}

						headerParams.colSpan = subs.length;
					}

					const params = {
						css: constants.CSS_TABLE_COLUMNS,
						heading: h,
						headerParams,
						subs,
						valueParams,
					};

					// add extra styling if comparing with another view
					this.addColumnComparisonStyling(ctx, params);

					if (this.onPathSpecParams(ctx, params, i) === false) {
						bEmpty = true;
						return;
					}

					const spec = new Spec(params);

					// filtering/sorting currently only supported in some cases when not a column matrix group
					if (!h.isMatrixGroup && !this.restrictQuery && !restrictHeadingQuery) {
						spec.setQuerySpec(this._getColumnQuerySpec(ctx, spec));
					}

					// apply column information to both spec and subs
					spec.setParam("columnIndex", i, true, true);
					spec.setParam("columnUrl", columnUrl, true, true);

					// update name of sub-spec if it's the only one and can be queried - the name will be presented as a batch filter option
					const firstSub = subs?.[0];
					if ((subs?.length === 1) && !this.shouldRenderLoneSubheader(firstSub) && firstSub.querySpec) {
						firstSub.name = spec.name;
					}

					specs.push(spec);
				});

				if (bEmpty) {
					return [];
				}
			} else {
				// matrix is 1 column x N rows
				var subs;

				if (h.isMatrixGroup) {
					subs = this._getColumnSpecSubs(ctx, h);

					if (!subs) {
						return [];
					}
				}

				var params = {
					css: constants.CSS_TABLE_COLUMNS,
					heading: h,
					subs,
				};

				if (this.onPathSpecParams(ctx, params) !== false) {
					specs.push(new Spec(params));
				}
			}

			return specs;
		},

		/**
		 * Return data to enter into the column nodes store
		 * @param {Context} ctx
		 * @param {C8Node[]} columnNodes
		 * @return {C8Node[]}
		 */
		getColumnStoreData(ctx, columnNodes) {
			return columnNodes;
		},

		/**
		 * Extension point to allow derived classes to indicate if column is only in the current view and not comparison view
		 * @param {Context} ctx
		 * @param {cnl} columnUrl
		 * @return {boolean}
		 */
		isNewComparisonColumn(ctx, columnUrl) {
			return false;
		},

		/**
		 * Extension point to allow derived classes to indicate if column is only in the comparison view and not the current view
		 * @param {Context} ctx
		 * @param {cnl} columnUrl
		 * @return {boolean}
		 */
		isOldComparisonColumn(ctx, columnUrl) {
			return false;
		},

		/**
		 * Extension point to allow derived classes to modify spec parameters
		 * @param {Context} ctx
		 * @param {Object} params
		 * @param {Number} [columnIndex]
		 * @return {boolean} - if exactly false is returned, then spec will be skipped
		 */
		onPathSpecParams: function(ctx, params, columnIndex) { },

		/**
		 * Extension point to allow derived classes to modify parameters of spec subs
		 * @param {Context} ctx
		 * @param {Object} params
		 * @param {Number} [columnIndex]
		 */
		onPathSubSpecParams: function(ctx, params, columnIndex) { },

		/**
		 * Add class name to spec params
		 * @param {Object} params
		 * @param {string} css
		 */
		_addColumnCss(params, css) {
			params.className = `${params.className || ""} ${css}`;
		},

		_getRowNodes: function(/*Context*/ctx, /*QueryResult*/qr, /*Spec*/c, /*Node*/node) {
			// summary: return row nodes for matrix
			function _onPathStep(nodeStart, nodesPrevious, nodesNext, idRelation, bChild) {
				if (_bOrdered) {
					var idRelationType = xml.nodeAttributeGetType(nodesPrevious[0], idRelation);
					_bOrdered = (idRelationType == xml.ATTR_REFVECTOR || idRelationType == xml.ATTR_REF);
				}

				if (!_bReadOnly) {
					_bReadOnly = !bChild || !reflection.isRelationEditable(nodesPrevious[0], idRelation, nodesNext[0], nodeStart, ctx);
				}
			}

			const {heading, matrixStandardPath} = c;

			if (matrixStandardPath) {
				// get nodes along path and store on context so that it is only computed once for each column node per row
				var urlPath = xml.getUrlPath(node.$URL, matrixStandardPath);
				var nodes;

				if (urlPath == ctx.matrixUrlPath) {
					nodes = ctx.matrixNodes;
					_bReadOnly = ctx.matrixReadOnly;
				} else {
					if (this.preserveVector) {
						var id = heading.m_id;
						var vectorUrls = node[id];
						if (vectorUrls) {
							nodes = ctx.cache.getNodes(vectorUrls);
						} else {
							nodes = [];
						}

						_bOrdered = true;
						_bReadOnly = reflection.isRelationEditable(node, id, nodes[0], node, ctx);
					} else {
						nodes = ctx.cache.getCachedPathNodes(node, matrixStandardPath, null, _onPathStep) || [];
					}

					if (!_bOrdered) {
						nodes = this._sortNodes(nodes, ctx.cache, this.sortAttr, this.sortPath);
					}

					ctx.matrixNodes = nodes;
					ctx.matrixUrlPath = urlPath;
					ctx.matrixReadOnly = _bReadOnly;
				}

				// apply ad hoc filter for matrix columns
				nodes = ctx.view.applyMatrixAdHocFilters(qr, c, nodes);

				// filter nodes for this cell
				return this._filterNodes(ctx, c, nodes);
			} else {
				return [node];
			}
		},

		/**
		 * Sort nodes
		 * @param {C8Node[]} nodes - nodes to sort
		 * @param {ICache} cache - cache of nodes
		 * @param {string?} sortAttr - sort nodes by given attribute, which could be a ref to another node
		 * @param {string?} sortPath - a path to the node with 'sortAttr' attribute
		 * @return {C8Node[]} sorted nodes
		 */
		_sortNodes(nodes, cache, sortAttr, sortPath) {
			if (!sortAttr) {
				return xml.sortNodes(nodes);
			}

			return csi.sort(nodes, (node) => extractSortValue(cache, node, sortAttr, sortPath));
		},

		render: function(/*Context*/ctx, /*QueryResult*/qr, /*Spec*/c, /*Number*/nColumn, /*DOMNode*/td) {
			// summary: traverse sub-headings with context
			var bAllow = !qr.isRoot() || this.bAllowOnRootNode;
			var n = c.subs?.length || 0;
			var node = ctx.getNode();
			var nodes;
			var sc;
			var i;

			_bOrdered = true;
			_bReadOnly = false;

			nodes = bAllow ? this._getRowNodes(ctx, qr, c, node) : [];

			// push new parameters on context - if only one node then set on context immediately - this is so that if
			// _getSpecNodes changes the nodes, that the original context is known
			ctx.pushParams({
				urlNode: (nodes && nodes.length == 1 && nodes[0]) ? nodes[0].$URL : null,
				urlParent: false,
				idRelation: null,
				[ctx.PARAM_ROW_URL]: node.$URL,
				[ctx.PARAM_COLUMN_URL]: c.columnUrl
			});

			// set context read only if path is read only
			if (_bReadOnly) {
				ctx.setReadOnly(true);
			}

			if (n) {
				for (i = 0; i < n; ++i) {
					sc = c.subs[i];
					td = i ? ctx.view.generateCell(sc, td.parentNode) : td;

					if (bAllow) {
						this._renderSpec(ctx, sc, nodes, nColumn, td);
					} else {
						sc.applyCss(td);
					}

					++nColumn;
				}
			} else if (bAllow) {
				this._renderSpec(ctx, c, nodes, nColumn, td);
				++nColumn;
			} else {
				c.applyCss(td);
				++nColumn;
			}

			ctx.pop();
			ctx.keyValue = null;
			return nColumn;
		},

		/**
		 * Return true if header is just showing the name of the nodes along the matrix path
		 * @param {Spec} subSpec
		 * @return {boolean}
		 */
		shouldRenderLoneSubheader(subSpec) {
			const {heading} = subSpec;
			return !this.hideLoneSubheader && heading && (heading.m_path || (heading.m_id != xml.ATTR_NAME));
		},

		_filterNodes: function(/*Context*/ctx, /*Spec*/spec, /*Node[]*/nodes) {
			// summary: apply any filters to nodes that are in matrix for given spec
			return nodes;
		},

		_getColumnNodes: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return array of nodes to be shown as columns if column matrix - return empty array if column matrix but no results
			return this.columnPath ? ctx.cache.getCachedPathNodes(ctx.getParent(), this.columnPath) : null;
		},

		_getColumnQuerySpec: function(/*Context*/ctx, /*Spec*/spec) {
			// summary: return query spec for column
			return csiHeading.getQuerySpec(ctx, spec);
		},

		_getColumnSubQuerySpec: function(/*Context*/ctx, /*Spec*/spec) {
			// summary: return query spec for column
			return csiHeading.getQuerySpec(ctx, spec);
		},

		_getColumnSpecHeaderParams: function(/*Context*/ctx, /*Heading*/h, /*Number*/index) {
			// summary: by default just render name of column node
			const {columnHeading} = this.config;
			let {columnHeadingSpec} = this;

			if (columnHeading && !columnHeadingSpec) {
				const specBuilder = new SpecBuilder(ctx.getNodeType(), ctx);
				columnHeadingSpec = this.columnHeadingSpec = specBuilder.getSpecForHeading(columnHeading);
			}

			if (columnHeadingSpec) {
				const div = html.create("div");
				csiHeading.toHtml(ctx, columnHeadingSpec.heading, div);

				// FIX - saving content to innerHTML does not work with images; instead just save entire div?
				return {innerHTML: div.innerHTML};
			}

			return {innerText: locale.getNodeConfiguredName(ctx.getNode(), ctx.cache, ctx.locale) || " "};
		},

		/**
		 * determine how to handle the heading with/without subs in the matrix, we push the spec of the heading directly to the array by default
		 * @param {Heading} h the current heading within the current matrix
		 * @param {Spec} sc the spec of this heading
		 * @param {object[]} subs the existing sub heading objects
		 * @param {object} params
		 */
		_onColumnSpecAddSub(h, sc, subs, params) {
			subs.push(sc);
		},

		_getColumnSpecSubs: function(/*Context*/ctx, /*Heading*/h, /*Number*/index) {
			// summary: return array of specs for matrix group
			var subs = [];

			h.m_subheadings?.forEach((sh) => {
				if (sh.matrix) {
					// only case now where there is matrix within another matrix is with Index processor;
					// this case does not yet support querying (sorting/filtering)
					subs = subs.concat(sh.getMatrixProcessor().getColumnSpecs(ctx, sh));
				} else {
					const params = {heading: sh};

					this.onPathSubSpecParams(ctx, params, index);

					const sc = new Spec(params);
					const headingParams = sc.heading.classRendererParams;
					const restrictHeadingQuery = headingParams && headingParams.restrictQuery;

					if (!this.restrictQuery && !restrictHeadingQuery) {
						sc.setQuerySpec(this._getColumnSubQuerySpec(ctx, sc));
					}

					this._onColumnSpecAddSub(sh, sc, subs, params);
				}
			});

			// if no sub-headings, then nothing to show
			if (!subs.length) {
				return null;
			}

			// post-process sub-specs
			_.each(subs, function(s) {
				// do not show the number of blanks vs non-blanks when filtering values through matrix group because they do not make sense
				var qs = s.querySpec;
				if (qs) {
					qs.notSortable = qs.omitBlankCounts = true;
				}
			});

			return subs;
		},

		_getSpecIndices: function(/*Context*/ctx, /*Spec*/spec, /*Node[]*/nodes) {
			// summary: return list of indices to apply with spec to given nodes
			return null;
		},

		_getSpecNodes: function(/*Context*/ctx, /*Spec*/spec, /*Node[]*/nodes) {
			// summary: return list of nodes that match current row and column, if different than given nodes
			if (this.preserveVector) {
				// if using vector, then return next node in array, which could be null
				var vectorNode = nodes.shift();
				return vectorNode ? [vectorNode] : [];
			}

			return null;
		},

		_renderSpec: function(/*Context*/ctx, /*Spec*/spec, /*Node[]*/nodes, /*Number*/nColumn, /*DOMNode*/td) {
			// summary: render spec to given cell
			// NOTE: _getSpecNodes() MUST always be called prior to _getSpecIndices() becase getSpecNodes() in SizeMatrix
			// creates _unselectedDim2Indices structure that is required in SizeMatrix->_getSpecIndices()
			var specNodes = this._getSpecNodes(ctx, spec, nodes);
			var specIndices = this._getSpecIndices(ctx, spec, nodes);
			if (specNodes) {
				ctx.pushParams();
				ctx.setNode(null);
				nodes = specNodes;
			}

			this._renderCell(ctx, spec, nColumn, td, nodes, specIndices);

			if (specNodes) {
				ctx.pop();
			}
		}
	});
});